/* ============================================================================
   Invitation to Tender — shared definitions.
   The six ITT sections the manager assembles (each AI-draftable), and the
   eight-item submission package every bidder completes as a checklist.
   ========================================================================== */
import type { InvitationToTender, IttSection, PricingBasis, Tender } from "./types";

export const ITT_SECTION_DEFS: { key: string; title: string; hint: string }[] = [
  { key: "letter", title: "Invitation letter", hint: "The formal letter inviting the bid — project, client, close date, how to respond." },
  { key: "scope", title: "Scope of works", hint: "What is being priced — packages, extents, exclusions." },
  { key: "projectInfo", title: "Project information", hint: "Site, size and delivery facts — inferred from the drawing register." },
  { key: "drawingsInstructions", title: "Instructions to bidders", hint: "Analyze the drawing set, addenda procedure, RFI window, pricing basis." },
  { key: "specifications", title: "Building specifications", hint: "Outline specification by discipline, each item referencing its source sheets (incl. MEP)." },
  { key: "bidderRequirements", title: "Bidder requirements", hint: "Qualification criteria — experience, capacity, bond, insurances, key personnel." },
  { key: "specialInstructions", title: "Special instructions & clarifications", hint: "Anything project-specific — site access & visits, working hours, phasing, tender validity, alternates policy, how queries are handled." },
];

/* --- pricing basis: BOQ issued vs bidder takes off quantities ----------- */
export const PRICING_BASIS_LABEL: Record<PricingBasis, string> = {
  boq: "Bill of Quantities provided",
  drawings: "No BOQ — bidders quantify the drawings",
};

/** The formal statement of the pricing basis, as it reads in the ITT and email. */
export function pricingBasisLine(basis: PricingBasis): string {
  return basis === "boq"
    ? "A Bill of Quantities is provided with the tender documents. Tenderers shall price against the BOQ as issued; quantities are the responsibility of the employer unless stated otherwise."
    : "No Bill of Quantities is provided. Tenderers are responsible for taking off their own quantities from the tender drawings and specifications, and the tender sum shall be deemed to cover the complete works shown.";
}

/** The pricing checklist item adapts to the basis. */
function pricingHint(basis: PricingBasis): string {
  return basis === "boq"
    ? "The issued Bill of Quantities, priced in full — every item rated, no omissions."
    : "Your own priced quantities taken off from the drawings — measured BOQ or schedule of rates covering the full scope.";
}

export const SUBMISSION_ITEMS: { key: string; label: string; hint: string }[] = [
  { key: "cover", label: "Cover letter", hint: "Signed letter on your letterhead confirming the offer and its validity period." },
  { key: "bidForm", label: "Completed bid form", hint: "The Form of Tender (FRM-01), completed and signed." },
  { key: "pricing", label: "Detailed pricing", hint: "Priced BOQ / schedule of rates covering the full scope." },
  { key: "scopeConfirm", label: "Scope confirmation", hint: "Statement confirming the works priced, with any exclusions listed." },
  { key: "experience", label: "Relevant experience", hint: "Comparable projects completed in the last 5 years, with references." },
  { key: "program", label: "Construction work program", hint: "Programme from award to completion, showing key milestones." },
  { key: "priceSummary", label: "Price summary", hint: "One-page summary of the tender amount and its major elements." },
  { key: "team", label: "Project team outline", hint: "Proposed site organisation and CVs of key personnel." },
];

/** SUBMISSION_ITEMS with the pricing hint matched to the tender's basis. */
export function submissionItems(basis: PricingBasis = "drawings"): { key: string; label: string; hint: string }[] {
  return SUBMISSION_ITEMS.map((x) => (x.key === "pricing" ? { ...x, hint: pricingHint(basis) } : x));
}

/** Blank ITT scaffold shown before anything is drafted. */
export function emptyItt(): InvitationToTender {
  return {
    sections: ITT_SECTION_DEFS.map((d) => ({ key: d.key, title: d.title, body: "" })),
    pricingBasis: "drawings",
    updatedAt: new Date().toISOString(),
  };
}

/** An ITT saved before a section existed gets the missing ones added blank,
    in definition order — keeps older drafts editable as the pack grows. */
export function normalizeItt(itt: InvitationToTender): InvitationToTender {
  return {
    ...itt,
    sections: ITT_SECTION_DEFS.map((d) =>
      itt.sections.find((s) => s.key === d.key) ?? { key: d.key, title: d.title, body: "" }),
  };
}

/** The ITT as one email/plain-text document (used when issuing to bidders). */
export function ittToText(t: Tender, itt: InvitationToTender): string {
  const basis = itt.pricingBasis ?? "drawings";
  const head = `INVITATION TO TENDER — ${t.name} (${t.code})\n${t.location} · for ${t.client.company}\n` +
    `Bids close: ${new Date(t.closeDate).toLocaleString()}` +
    (itt.constructionStart ? `\nTargeted construction start: ${itt.constructionStart}` : "") +
    `\nPricing basis: ${PRICING_BASIS_LABEL[basis]}\n`;
  const body = itt.sections
    .filter((s: IttSection) => s.body.trim())
    .map((s, i) => `\n${i + 1}. ${s.title.toUpperCase()}\n${s.body.trim()}`)
    .join("\n");
  const pack = `\n\nPRICING BASIS\n${pricingBasisLine(basis)}` +
    "\n\nREQUIRED SUBMISSION PACKAGE — submit all items via your bidder room:\n" +
    submissionItems(basis).map((x, i) => `  ${i + 1}. ${x.label} — ${x.hint}`).join("\n");
  return head + body + pack;
}
